import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Gauge, Landmark, Percent, Smile, Target, UserRoundCog, Users } from 'lucide-react'
import { formatCompactMoney } from '@/lib/format'
import type { KpiReport, KpiStatus } from '@/types/analytics'

const STATUS_CLASS: Record<KpiStatus, string> = {
  green: 'text-success',
  yellow: 'text-warning',
  red: 'text-destructive',
  none: 'text-muted-foreground',
}

const STATUS_LABEL: Record<KpiStatus, string> = {
  green: 'On target',
  yellow: 'Watch',
  red: 'Red flag',
  none: 'No data',
}

function KpiCard({
  title,
  value,
  target,
  hint,
  status,
  icon: Icon,
  loading,
}: {
  title: string
  value: string
  target: string
  hint?: string
  status: KpiStatus
  icon: React.ComponentType<{ className?: string }>
  loading?: boolean
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs font-medium text-muted-foreground">{title}</p>
            {loading ? (
              <Skeleton className="mt-1.5 h-7 w-24" />
            ) : (
              <p className={`truncate text-2xl font-bold ${STATUS_CLASS[status]}`}>{value}</p>
            )}
            <p className="mt-0.5 text-xs text-muted-foreground">{target}</p>
            {hint && !loading && (
              <p className="mt-1 truncate text-xs text-muted-foreground">{hint}</p>
            )}
            {!loading && status !== 'none' && (
              <p className={`mt-1 text-xs font-medium ${STATUS_CLASS[status]}`}>
                {STATUS_LABEL[status]}
              </p>
            )}
          </div>
          <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
        </div>
      </CardContent>
    </Card>
  )
}

const pct = (v: number) => `${v.toFixed(1)}%`

// The seven KPI cards of the main dashboard. Values, targets and the
// green/yellow/red status all come from the backend report.
export function KpiCards({ report, isLoading }: { report?: KpiReport; isLoading: boolean }) {
  const k = report?.kpis

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <KpiCard
        title="Revenue vs. plan"
        value={k ? pct(k.revenue_vs_plan.pct) : '—'}
        target="Target ≥ 100% of plan"
        hint={
          k
            ? `${formatCompactMoney(k.revenue_vs_plan.actual)} vs ${formatCompactMoney(k.revenue_vs_plan.budget)} budget`
            : undefined
        }
        status={k?.revenue_vs_plan.status ?? 'none'}
        icon={Target}
        loading={isLoading}
      />
      <KpiCard
        title="Net profit margin"
        value={k ? pct(k.net_profit_margin.margin_pct) : '—'}
        target="Target 4–6% (8%+ = top quartile)"
        hint={
          k
            ? `${formatCompactMoney(k.net_profit_margin.net_income)} net on ${formatCompactMoney(k.net_profit_margin.revenue)}`
            : undefined
        }
        status={k?.net_profit_margin.status ?? 'none'}
        icon={Percent}
        loading={isLoading}
      />
      <KpiCard
        title="Absorption rate"
        value={k ? pct(k.absorption_rate.pct) : '—'}
        target="Target ≥ 100% of overhead"
        hint={
          k
            ? `Service ${formatCompactMoney(k.absorption_rate.service_gross)} + parts ${formatCompactMoney(k.absorption_rate.parts_gross)} vs overhead ${formatCompactMoney(k.absorption_rate.overhead)}`
            : undefined
        }
        status={k?.absorption_rate.status ?? 'none'}
        icon={Gauge}
        loading={isLoading}
      />
      <KpiCard
        title="Floor plan interest"
        value={k ? pct(k.floor_plan_interest.pct) : '—'}
        target="Target < 4% of gross"
        hint={
          k
            ? `${formatCompactMoney(k.floor_plan_interest.interest)} interest on ${formatCompactMoney(k.floor_plan_interest.total_gross)} gross`
            : undefined
        }
        status={k?.floor_plan_interest.status ?? 'none'}
        icon={Landmark}
        loading={isLoading}
      />
      <KpiCard
        title="CSI / NPS"
        value={k ? pct(k.customer_satisfaction.csi) : '—'}
        target="CSI ≥ 90% · NPS 50+"
        hint={
          k && k.customer_satisfaction.responses > 0
            ? `NPS ${k.customer_satisfaction.nps.toFixed(0)} · ${k.customer_satisfaction.responses} responses`
            : undefined
        }
        status={k?.customer_satisfaction.status ?? 'none'}
        icon={Smile}
        loading={isLoading}
      />
      <KpiCard
        title="Employee turnover"
        value={k ? pct(k.employee_turnover.pct) : '—'}
        target="Target < 20% annualized"
        hint={
          k
            ? `${k.employee_turnover.separations} separations · avg ${k.employee_turnover.avg_headcount.toFixed(1)} FTE`
            : undefined
        }
        status={k?.employee_turnover.status ?? 'none'}
        icon={Users}
        loading={isLoading}
      />
      <KpiCard
        title="Revenue per employee"
        value={k ? formatCompactMoney(k.revenue_per_employee.amount) : '—'}
        target="Target $450–600K / FTE"
        hint={
          k
            ? `${formatCompactMoney(k.revenue_per_employee.ttm_revenue)} TTM / ${k.revenue_per_employee.avg_headcount.toFixed(1)} FTE`
            : undefined
        }
        status={k?.revenue_per_employee.status ?? 'none'}
        icon={UserRoundCog}
        loading={isLoading}
      />
    </div>
  )
}
